// /////////////////////////////////////////////////////////////
// [ファイル] SfaPipelineDashboard.tsx
// [概要] 案件推進管理（先取管理）ダッシュボード
//        週次/月次断面のランク別推進残・受注残のトレンド表示、
//        内訳（拠点別/部署別/担当者別）、失注分析、セルドリルダウンを提供する
// /////////////////////////////////////////////////////////////
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TextField,
  MenuItem,
  Autocomplete,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Link,
} from '@mui/material';
import * as Icons from '../constants/icons';
import LoadingView from '../components/LoadingView';
import SalesProjectPhaseChip from '../components/SalesProjectPhaseChip';
import PipelineFunnelBoard from '../components/PipelineFunnelBoard';
import { useNotification } from '../providers/NotificationProvider';
import { useMasterData } from '../providers/MasterDataProvider';
import {
  STANDARD_CTA_BUTTON_SX,
  STANDARD_TEXT_BUTTON_SX,
} from '../constants/buttonStyles';
import {
  useSfaPipeline,
  PipelineSummaryRow,
  PipelineProjectRow,
} from '../hooks/useSfaPipeline';

// 額面金額を「万円」表記へ丸めて表示する
const fmtMan = (v?: number | null): string =>
  v ? `${Math.round(v / 10000).toLocaleString()}万` : '-';

// 断面日の列見出し（yy/MM/dd）
const fmtDate = (d: string): string => d.slice(2).replace(/-/g, '/');

// マトリクス行の定義（見込み → D→A → 推進残計 → S受注残）
interface MatrixRowDef {
  key: string;           // summary 行との突合キー（rank_code または aggregate_class）
  label: string;
  phase?: string;        // ドリルダウン表示用のフェーズ名
  aggregateClass?: string;
  rankCode?: string;
  isTotal?: boolean;
}

export default function SfaPipelineDashboard() {
  const navigate = useNavigate();
  const notify = useNotification();
  const { staffList, branchList } = useMasterData();
  const {
    filters, setFilters, summary, breakdown, lostRows, phases,
    loading, capturing, error, captureSnapshot, fetchProjects,
  } = useSfaPipeline();

  // ドリルダウンダイアログ
  const [drillOpen, setDrillOpen] = useState(false);
  const [drillTitle, setDrillTitle] = useState('');
  const [drillRows, setDrillRows] = useState<PipelineProjectRow[]>([]);
  const [drillLoading, setDrillLoading] = useState(false);

  // フェーズマスタからマトリクス行を構成する
  const matrixRows: MatrixRowDef[] = useMemo(() => {
    const rows: MatrixRowDef[] = phases
      .filter((p) => p.aggregate_class === 'prospect' || p.aggregate_class === 'pipeline')
      .sort((a, b) => a.sort_order - b.sort_order)
      .map((p) => ({
        key: p.rank_code || p.aggregate_class,
        label: p.rank_code ? `${p.rank_code}ランク` : '見込み（ランク前）',
        phase: p.phase,
        aggregateClass: p.aggregate_class,
        rankCode: p.rank_code || undefined,
      }));
    rows.push({ key: '__pipeline_total__', label: '推進残 計（D〜A）', isTotal: true, aggregateClass: 'pipeline' });
    const backlog = phases.find((p) => p.aggregate_class === 'backlog');
    if (backlog) {
      rows.push({
        key: backlog.rank_code || 'backlog',
        label: `${backlog.rank_code}ランク（受注残）`,
        phase: backlog.phase,
        aggregateClass: 'backlog',
        rankCode: backlog.rank_code || undefined,
      });
    }
    return rows;
  }, [phases]);

  // 断面日リスト（昇順）
  const snapshotDates: string[] = useMemo(
    () => Array.from(new Set(summary.map((r) => r.snapshot_date))).sort(),
    [summary]
  );

  // (断面日, 行キー) → 集計行 のマップと推進残計
  const cellMap = useMemo(() => {
    const map = new Map<string, PipelineSummaryRow>();
    const totals = new Map<string, { project_count: number; amount_sum: number; weighted_sum: number }>();
    summary.forEach((r) => {
      map.set(`${r.snapshot_date}|${r.rank_code || r.aggregate_class}`, r);
      if (r.aggregate_class === 'pipeline') {
        const t = totals.get(r.snapshot_date) || { project_count: 0, amount_sum: 0, weighted_sum: 0 };
        t.project_count += r.project_count;
        t.amount_sum += r.amount_sum;
        t.weighted_sum += r.weighted_sum;
        totals.set(r.snapshot_date, t);
      }
    });
    return { map, totals };
  }, [summary]);

  // 内訳（最新断面日）
  const latestDate = snapshotDates[snapshotDates.length - 1];
  const breakdownRows = useMemo(() => {
    if (!filters.dimension || !latestDate) return [];
    const byDim = new Map<string, { dimCode: string | null; dimName: string; cells: Map<string, PipelineSummaryRow>; total: number }>();
    breakdown
      .filter((r) => r.snapshot_date === latestDate)
      .forEach((r) => {
        const name = r.dim_name || '未設定';
        const entry = byDim.get(name) || { dimCode: r.dim_code, dimName: name, cells: new Map(), total: 0 };
        entry.cells.set(r.rank_code || r.aggregate_class, r);
        if (r.aggregate_class === 'pipeline') entry.total += r.amount_sum;
        byDim.set(name, entry);
      });
    return Array.from(byDim.values()).sort((a, b) => b.total - a.total);
  }, [breakdown, filters.dimension, latestDate]);

  // ドリルダウンを開く
  const openDrilldown = async (
    snapshotDate: string,
    row: MatrixRowDef,
    dimFilter?: { branch?: string; department?: string; staffId?: string },
    dimLabel?: string,
  ) => {
    setDrillTitle(`${fmtDate(snapshotDate)} ${dimLabel ? `${dimLabel} ` : ''}${row.label}`);
    setDrillOpen(true);
    setDrillLoading(true);
    try {
      const rows = await fetchProjects(
        snapshotDate,
        row.isTotal ? 'pipeline' : row.aggregateClass,
        row.isTotal ? undefined : row.rankCode,
        dimFilter,
      );
      setDrillRows(rows);
    } catch (err: any) {
      notify.error(err.message || '明細の取得に失敗しました');
      setDrillRows([]);
    } finally {
      setDrillLoading(false);
    }
  };

  const handleCapture = async () => {
    const result = await captureSnapshot();
    if (result.ok) notify.success(result.message);
    else notify.error(result.message);
  };

  return (
    <Container maxWidth="xl" sx={{ py: 3, boxSizing: 'border-box' }}>
      {/* ヘッダー */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
        <Box
          sx={{
            bgcolor: 'primary.light', color: 'white', width: 28, height: 28,
            borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <Icons.TrendingUp sx={{ fontSize: '1.25rem' }} />
        </Box>
        <Typography variant="h6" color="text.secondary">案件推進管理（推進残）</Typography>
        <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
          <Button
            startIcon={<Icons.Refresh />}
            onClick={handleCapture}
            disabled={capturing || loading}
            sx={STANDARD_TEXT_BUTTON_SX}
          >
            {capturing ? '取得中...' : '現時点の断面を取得'}
          </Button>
        </Box>
      </Box>

      {/* フィルター */}
      <Paper sx={{ p: 1.5, mb: 2, borderRadius: 2, display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          label="開始日" type="date" size="small" value={filters.fromDate}
          onChange={(e) => setFilters((prev) => ({ ...prev, fromDate: e.target.value }))}
          slotProps={{ inputLabel: { shrink: true } }}
          sx={{ width: 160 }}
        />
        <TextField
          label="終了日" type="date" size="small" value={filters.toDate}
          onChange={(e) => setFilters((prev) => ({ ...prev, toDate: e.target.value }))}
          slotProps={{ inputLabel: { shrink: true } }}
          sx={{ width: 160 }}
        />
        <TextField
          select label="支店" size="small" value={filters.branch}
          onChange={(e) => setFilters((prev) => ({ ...prev, branch: e.target.value }))}
          sx={{ width: 160 }}
        >
          <MenuItem value="">すべて</MenuItem>
          {branchList.map((b: any) => (
            <MenuItem key={b.branch_code} value={b.branch_code}>{b.branch_name}</MenuItem>
          ))}
        </TextField>
        <Autocomplete
          options={staffList}
          getOptionLabel={(o: any) => o.staff_name || ''}
          value={staffList.find((s: any) => s.staff_id === filters.staffId) || null}
          onChange={(e, v: any) => setFilters((prev) => ({ ...prev, staffId: v ? v.staff_id : '' }))}
          isOptionEqualToValue={(o: any, v: any) => o.staff_id === v.staff_id}
          renderInput={(params) => <TextField {...params} label="担当者" size="small" />}
          sx={{ width: 200 }}
        />
        <TextField
          select label="内訳軸" size="small" value={filters.dimension}
          onChange={(e) => setFilters((prev) => ({ ...prev, dimension: e.target.value as any }))}
          sx={{ width: 160 }}
        >
          <MenuItem value="">なし</MenuItem>
          <MenuItem value="branch">拠点別</MenuItem>
          <MenuItem value="department">部署別</MenuItem>
          <MenuItem value="staff">担当者別</MenuItem>
        </TextField>
      </Paper>

      {loading && <LoadingView message="推進残データを読み込み中..." height="50vh" size={50} />}

      {!loading && error && (
        <Typography color="error" sx={{ py: 4, textAlign: 'center' }}>{error}</Typography>
      )}

      {!loading && !error && snapshotDates.length === 0 && (
        <Paper sx={{ p: 4, borderRadius: 2, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            指定期間に断面データがありません。週次（毎週月曜）・月次（月末）の自動取得を待つか、現時点の断面を手動取得してください。
          </Typography>
          <Button startIcon={<Icons.Refresh />} onClick={handleCapture} disabled={capturing} sx={STANDARD_CTA_BUTTON_SX}>
            現時点の断面を取得
          </Button>
        </Paper>
      )}

      {!loading && !error && snapshotDates.length > 0 && (
        <>
          {/* 案件推進ボード（最新断面のファネル表示） */}
          {latestDate && (
            <PipelineFunnelBoard
              snapshotDate={latestDate}
              summary={summary}
              lostRows={lostRows}
              phases={phases}
              onColumnClick={(col) => openDrilldown(latestDate, col)}
            />
          )}

          {/* ランク x 断面日 マトリクス */}
          <Paper sx={{ borderRadius: 2, mb: 2, overflow: 'hidden' }}>
            <Box sx={{ px: 2, pt: 1.5, pb: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="subtitle2" color="text.secondary">時系列トレンド（ランク×断面日・額面）</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                上段 額面合計 / 下段 件数・加重（額面×確度）。セルクリックで明細
              </Typography>
            </Box>
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: 640 }}>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap', minWidth: 170 }}>ランク（フェーズ）</TableCell>
                    {snapshotDates.map((d) => (
                      <TableCell key={d} align="right" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                        {fmtDate(d)}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {matrixRows.map((row) => (
                    <TableRow
                      key={row.key}
                      sx={row.isTotal
                        ? { bgcolor: 'grey.100', '& td': { fontWeight: 'bold' } }
                        : row.aggregateClass === 'backlog'
                          ? { bgcolor: '#f1f8e9' }
                          : undefined}
                    >
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: row.isTotal ? 'bold' : 500 }}>
                            {row.label}
                          </Typography>
                          {row.phase && <SalesProjectPhaseChip phase={row.phase} sx={{ height: 18, fontSize: '0.65rem' }} />}
                        </Box>
                      </TableCell>
                      {snapshotDates.map((d) => {
                        const cell = row.isTotal
                          ? cellMap.totals.get(d)
                          : cellMap.map.get(`${d}|${row.key}`);
                        return (
                          <TableCell
                            key={d}
                            align="right"
                            onClick={() => cell && openDrilldown(d, row)}
                            sx={{ cursor: cell ? 'pointer' : 'default', '&:hover': cell ? { bgcolor: 'action.hover' } : undefined }}
                          >
                            {cell ? (
                              <>
                                <Typography variant="body2" sx={{ fontWeight: 'inherit' }}>
                                  {fmtMan(cell.amount_sum)}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                                  {cell.project_count}件 / 加重 {fmtMan(cell.weighted_sum)}
                                </Typography>
                              </>
                            ) : (
                              <Typography variant="caption" color="text.disabled">-</Typography>
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Paper>

          {/* 内訳（最新断面） */}
          {filters.dimension && latestDate && (
            <Paper sx={{ borderRadius: 2, mb: 2, overflow: 'hidden' }}>
              <Box sx={{ px: 2, pt: 1.5, pb: 0.5 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  内訳 {filters.dimension === 'branch' ? '拠点別' : filters.dimension === 'department' ? '部署別' : '担当者別'}（最新断面 {fmtDate(latestDate)}・額面）
                </Typography>
              </Box>
              <Box sx={{ overflowX: 'auto' }}>
                <Table size="small" sx={{ minWidth: 640 }}>
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                      <TableCell sx={{ fontWeight: 'bold', minWidth: 140 }}>
                        {filters.dimension === 'branch' ? '拠点' : filters.dimension === 'department' ? '部署' : '担当者'}
                      </TableCell>
                      {matrixRows.map((row) => (
                        <TableCell key={row.key} align="right" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                          {row.isTotal ? '推進残 計' : row.rankCode ? `${row.rankCode}` : '見込み'}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {breakdownRows.map((dim) => (
                      <TableRow key={dim.dimName}>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{dim.dimName}</TableCell>
                        {matrixRows.map((row) => {
                          const cell = row.isTotal ? undefined : dim.cells.get(row.key);
                          const value = row.isTotal ? dim.total : cell?.amount_sum;
                          const dimFilter = filters.dimension === 'branch'
                            ? { branch: dim.dimCode || undefined }
                            : filters.dimension === 'department'
                              ? { department: dim.dimCode || undefined }
                              : { staffId: dim.dimCode || undefined };
                          const clickable = row.isTotal ? dim.total > 0 : !!cell;
                          return (
                            <TableCell
                              key={row.key}
                              align="right"
                              onClick={() => clickable && openDrilldown(latestDate, row, dimFilter, dim.dimName)}
                              sx={{
                                cursor: clickable ? 'pointer' : 'default',
                                fontWeight: row.isTotal ? 'bold' : undefined,
                                '&:hover': clickable ? { bgcolor: 'action.hover' } : undefined,
                              }}
                            >
                              {value ? fmtMan(value) : <Typography variant="caption" color="text.disabled">-</Typography>}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </Paper>
          )}

          {/* 失注分析 */}
          <Paper sx={{ borderRadius: 2, mb: 2, overflow: 'hidden' }}>
            <Box sx={{ px: 2, pt: 1.5, pb: 0.5 }}>
              <Typography variant="subtitle2" color="text.secondary">
                失注分析（期間内の失注遷移。どのランクから・どの理由で失注したか）
              </Typography>
            </Box>
            {lostRows.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
                期間内に失注の記録はありません。（失注理由の記録は 2026-08-21 以降のフェーズ変更が対象です）
              </Typography>
            ) : (
              <Box sx={{ overflowX: 'auto' }}>
                <Table size="small" sx={{ minWidth: 560 }}>
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                      <TableCell sx={{ fontWeight: 'bold' }}>失注前フェーズ（ランク）</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>失注理由</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>件数</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>失注金額（額面）</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {lostRows.map((r, idx) => (
                      <TableRow key={idx}>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {r.rank_before && <Chip label={`${r.rank_before}ランク`} size="small" sx={{ height: 20, fontSize: '0.7rem' }} />}
                            <SalesProjectPhaseChip phase={r.phase_before || undefined} />
                            {!r.phase_before && <Typography variant="caption" color="text.secondary">不明</Typography>}
                          </Box>
                        </TableCell>
                        <TableCell>{r.lost_reason_name}</TableCell>
                        <TableCell align="right">{r.lost_count}</TableCell>
                        <TableCell align="right">{fmtMan(r.lost_amount_sum)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}
          </Paper>
        </>
      )}

      {/* ドリルダウンダイアログ */}
      <Dialog open={drillOpen} onClose={() => setDrillOpen(false)} fullWidth maxWidth="lg">
        <DialogTitle sx={{ py: 0.75, display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Icons.ListAlt sx={{ fontSize: 18, color: 'primary.main' }} />
          <Typography component="span" sx={{ fontSize: '0.875rem', fontWeight: 'bold' }}>
            {drillTitle} の案件明細
          </Typography>
          <IconButton size="small" onClick={() => setDrillOpen(false)} sx={{ ml: 'auto', p: 0.5 }}>
            <Icons.Close sx={{ fontSize: 18 }} />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {drillLoading ? (
            <LoadingView message="明細を読み込み中..." height="200px" size={40} />
          ) : drillRows.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              該当する案件はありません。
            </Typography>
          ) : (
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: 760 }}>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>案件名</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>フェーズ</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>金額（額面）</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>加重</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>受注予定</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>担当者</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>支店</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {drillRows.map((r) => (
                    <TableRow key={r.project_id} hover>
                      <TableCell sx={{ maxWidth: 280 }}>
                        <Link
                          component="button"
                          type="button"
                          onClick={() => navigate(`/salesprojectsmanagement/${r.project_id}`)}
                          sx={{
                            textDecoration: 'none', fontSize: '0.8125rem', fontWeight: 600, textAlign: 'left',
                            '&:hover': { textDecoration: 'underline' },
                          }}
                        >
                          {r.project_name || r.project_id}
                        </Link>
                      </TableCell>
                      <TableCell><SalesProjectPhaseChip phase={r.phase} /></TableCell>
                      <TableCell align="right">{r.amount != null ? `${Number(r.amount).toLocaleString()} 円` : '-'}</TableCell>
                      <TableCell align="right">{fmtMan(r.weighted_amount)}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {r.expected_order_year || r.expected_order_month
                          ? `${r.expected_order_year ? `${r.expected_order_year}年` : ''}${r.expected_order_month ? `${r.expected_order_month}月` : ''}`
                          : '-'}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{r.main_staff_name || '-'}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{r.main_staff_branch_name || '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1 }}>
          <Button onClick={() => setDrillOpen(false)} sx={STANDARD_TEXT_BUTTON_SX}>閉じる</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
